import React, { useEffect, useState } from "react";
import axios from "axios";
import { COURSE_1, COURSE_2, BUNDLE, SHARED } from "../config/iGetToCourse";

/**
 * UNIVERSAL POST-PURCHASE SUCCESS PAGE
 *
 * Routes:
 *   /i-get-to-success         → Course 1 success (upsells Course 2)
 *   /i-get-to-deeper-success  → Course 2 success (upsells Bundle / Book)
 *   /i-get-to-bundle-success  → Bundle success (delivers BOTH PDFs)
 *
 * Detects which product the buyer paid for from the URL pathname.
 * Page is noindex,nofollow.
 */

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  gold: "#d4af6a",
  cream: "#efeae7",
  mint: "#5EEAD4",
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const detectProduct = () => {
  const path = window.location.pathname;
  if (path.includes("bundle-success")) return BUNDLE;
  if (path.includes("deeper-success")) return COURSE_2;
  return COURSE_1;
};

export default function IGetToSuccessPage() {
  const [product, setProduct] = useState(COURSE_1);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tagged, setTagged] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const p = detectProduct();
    setProduct(p);
    document.title = `Your ${p.shortName} is here · Temple Franklin`;

    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    let stripeSessionId = "";
    let overrideValue = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const e = params.get("customer_email") || params.get("email");
      if (e) setEmail(e);
      // Stripe Payment Link success URL can be templated with
      // ?session_id={CHECKOUT_SESSION_ID}&value={AMOUNT_TOTAL}
      stripeSessionId = params.get("session_id") || params.get("transaction_id") || "";
      const rawValue = params.get("value") || params.get("amount");
      if (rawValue) {
        // Stripe sends amount in cents; treat >100 numeric-only as cents
        const n = Number(rawValue);
        if (!Number.isNaN(n)) overrideValue = n > 100 ? n / 100 : n;
      }
    } catch (_) { /* URL parse guard */ }

    // ── GA4 PURCHASE EVENT ────────────────────────────────────────────────
    // Fire once per page load. De-dupe on transaction_id in sessionStorage
    // so a refresh doesn't double-count.
    try {
      const txnId =
        stripeSessionId ||
        `${p.id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const dedupeKey = `ga4_purchase_${txnId}`;
      const alreadyFired =
        typeof window !== "undefined" && window.sessionStorage
          ? sessionStorage.getItem(dedupeKey)
          : null;
      if (!alreadyFired && typeof window !== "undefined" && typeof window.gtag === "function") {
        const value = overrideValue != null ? overrideValue : p.price;
        window.gtag("event", "purchase", {
          transaction_id: txnId,
          value: value,
          currency: "USD",
          items: [
            {
              item_id: p.id,
              item_name: p.name,
              item_category: p.id === "bundle" ? "bundle" : "course",
              price: value,
              quantity: 1,
            },
          ],
        });
        if (window.sessionStorage) sessionStorage.setItem(dedupeKey, "1");

        // Meta Pixel — Purchase (safe no-op until pixel base loads)
        if (typeof window.fbq === "function") {
          window.fbq("track", "Purchase", {
            value: value,
            currency: "USD",
            content_ids: [p.id],
            content_name: p.name,
            content_type: "product",
          });
        }
      }
      // Optional: also fire on Pinterest if tag is active
      if (typeof window !== "undefined" && typeof window.pintrk === "function") {
        window.pintrk("track", "checkout", {
          value: overrideValue != null ? overrideValue : p.price,
          order_quantity: 1,
          currency: "USD",
          line_items: [{ product_name: p.name, product_id: p.id, product_price: overrideValue != null ? overrideValue : p.price }],
        });
      }
    } catch (_) { /* analytics guard */ }

    return () => {
      const stale = document.querySelector('meta[name="robots"]');
      if (stale) stale.remove();
    };
  }, []);

  const handleTag = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || "Friend",
        group_id: product.buyerGroup,
      });
      setTagged(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 || status === 409) {
        setTagged(true);
      } else {
        setErrorMsg(
          err?.response?.data?.detail ||
            "Couldn't tag your email this moment — no worries, your download is ready below."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Build the download blocks (Bundle gets two; Course 1/2 get one)
  const downloads =
    product.id === "bundle"
      ? [
          { ...COURSE_1, label: "Course 1 — Start Living I GET TO" },
          { ...COURSE_2, label: "Course 2 — The Deeper Return" },
        ]
      : [{ ...product, label: product.shortName }];

  // Decide the cross-sell shown at bottom
  let crossSell = null;
  if (product.id === "course-1") {
    crossSell = {
      eyebrow: "When you're ready for more",
      lede:
        "Course 2 — The Deeper Return — picks up where this leaves off. 18 pages of integration work, somatic literacy, legacy and the Long Game. $8.88, like this one.",
      ctaLabel: "See The Deeper Return →",
      href: COURSE_2.landingUrl,
    };
  } else if (product.id === "course-2") {
    crossSell = {
      eyebrow: "Did you start with the Bundle?",
      lede:
        "If you bought Course 2 alone, grab the 11:11 Bundle to add Course 1 — it's still cheaper than two singles. Otherwise, the paperback opens mid-June.",
      ctaLabel: "See the 11:11 Bundle →",
      href: BUNDLE.landingUrl,
    };
  } else {
    crossSell = {
      eyebrow: "When you finish the 38 pages",
      lede:
        "The full book (paperback, 141 pages, in color) opens mid-June. Same method, the whole way through — the A.C.T. NOW framework, the Personal Code of Conduct, the deep work.",
      ctaLabel: `See the book (${SHARED.bookPriceDisplay}) →`,
      href: SHARED.bookUrl,
    };
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{
        background: `linear-gradient(160deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
        color: BRAND.cream,
      }}
      data-testid={`${product.id}-success-page`}
    >
      <div className="max-w-2xl w-full text-center">
        <p
          className="font-sans text-xs tracking-[0.4em] uppercase mb-5"
          style={{ color: BRAND.gold }}
        >
          You did it ✦
        </p>

        <h1
          className="font-serif tracking-tight leading-[1.05] mb-6"
          style={{ fontSize: "clamp(2.2rem, 5vw, 3.6rem)" }}
          data-testid="success-headline"
        >
          Your {product.id === "bundle" ? "bundle" : "course"}{" "}
          <span style={{ color: BRAND.mint, fontStyle: "italic" }}>is here.</span>
        </h1>

        <p
          className="font-serif italic text-lg md:text-xl leading-relaxed mb-10 max-w-xl mx-auto"
          style={{ color: `${BRAND.cream}dd` }}
        >
          {product.id === "bundle"
            ? "All 38 pages, yours forever. Two PDFs below. Print them, save them, read them right in your browser."
            : `${product.name}. ${product.pages} pages, yours forever. Tap below to download.`}
        </p>

        {/* Download buttons */}
        <div className="flex flex-col gap-4 items-center mb-10">
          {downloads.map((d, i) => (
            <a
              key={d.id}
              href={d.pdfDownload}
              download={d.pdfFileName}
              className="inline-flex items-center gap-3 px-9 py-5 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] hover:shadow-2xl"
              style={{
                background: i === 0 ? BRAND.mint : `${BRAND.cream}11`,
                color: i === 0 ? BRAND.deep : BRAND.cream,
                border: i === 0 ? "none" : `1px solid ${BRAND.mint}`,
                fontSize: "16px",
              }}
              data-testid={`download-${d.id}`}
            >
              Download {d.label}
              <span style={{ fontSize: "20px" }}>↓</span>
            </a>
          ))}
        </div>

        {/* Soft MailerLite tag form */}
        {!tagged ? (
          <div
            className="p-7 rounded-2xl max-w-xl mx-auto mb-10"
            style={{ background: `${BRAND.cream}11`, border: `1px solid ${BRAND.gold}44` }}
            data-testid="tag-form-wrapper"
          >
            <p
              className="font-sans text-xs tracking-[0.3em] uppercase font-semibold mb-3"
              style={{ color: BRAND.gold }}
            >
              One more thing
            </p>
            <p
              className="font-serif italic text-base leading-relaxed mb-5"
              style={{ color: `${BRAND.cream}dd` }}
            >
              Want me to email you the same {product.id === "bundle" ? "links" : "link"} as a
              backup — plus a short note when the paperback opens? Drop your name and email below.
            </p>
            <form onSubmit={handleTag} className="flex flex-col sm:flex-row gap-3" data-testid="tag-form">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="sm:w-40 px-4 py-3 rounded-full font-sans text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.95)", color: BRAND.deep }}
                data-testid="tag-first-name"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your email"
                className="flex-1 px-4 py-3 rounded-full font-sans text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.95)", color: BRAND.deep }}
                data-testid="tag-email"
              />
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] disabled:opacity-60 whitespace-nowrap"
                style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "14px" }}
                data-testid="tag-submit"
              >
                {submitting ? "Sending…" : "Email me a backup"}
              </button>
            </form>
            {errorMsg && (
              <p className="font-sans text-xs mt-3" style={{ color: "#fda4af" }}>
                {errorMsg}
              </p>
            )}
          </div>
        ) : (
          <div
            className="p-7 rounded-2xl max-w-xl mx-auto mb-10"
            style={{ background: `${BRAND.mint}1a`, border: `1px solid ${BRAND.mint}` }}
            data-testid="tag-success"
          >
            <p className="font-serif italic text-lg" style={{ color: BRAND.cream }}>
              On the list. ✦ I'll send you backup link{product.id === "bundle" ? "s" : ""} and a
              quiet note when the paperback opens.
            </p>
          </div>
        )}

        {/* Cross-sell card */}
        <div
          className="p-7 rounded-2xl max-w-xl mx-auto"
          style={{
            background: `${BRAND.cream}0a`,
            borderLeft: `3px solid ${BRAND.gold}`,
            textAlign: "left",
          }}
          data-testid="success-crosssell"
        >
          <p
            className="font-sans text-xs tracking-[0.3em] uppercase font-semibold mb-3"
            style={{ color: BRAND.gold }}
          >
            {crossSell.eyebrow}
          </p>
          <p
            className="font-serif italic text-base leading-relaxed mb-4"
            style={{ color: `${BRAND.cream}dd` }}
          >
            {crossSell.lede}
          </p>
          <a
            href={crossSell.href}
            className="inline-flex items-center gap-2 font-sans text-sm font-semibold tracking-wide underline underline-offset-4 hover:underline"
            style={{ color: BRAND.mint }}
            data-testid="success-crosssell-link"
          >
            {crossSell.ctaLabel}
          </a>
        </div>

        <a
          href="/"
          className="inline-block mt-10 font-sans text-sm tracking-wide underline-offset-4 hover:underline"
          style={{ color: `${BRAND.cream}aa` }}
          data-testid="back-home-link"
        >
          ← Back to mindbodyspirithygienetools.com
        </a>
      </div>
    </div>
  );
}
